import React, { useEffect, useState, useMemo, useRef } from 'react';
import StoreFinder from "../StoreFinder/StoreFinder";

const formatPhoneNumber = (phoneNumber: string) => {
  if (!phoneNumber) return "";
  
  // Remove any non-digit characters
  const digits = phoneNumber.replace(/\D/g, "");
  
  // Handle Australian mobile numbers starting with 61 (country code)
  if (digits.startsWith("61") && digits.length === 11) {
    const withoutCountryCode = digits.substring(2);
    // Format as 02 9137 4974 for landlines starting with 2-9
    if (withoutCountryCode.startsWith("2") || withoutCountryCode.startsWith("3") || 
        withoutCountryCode.startsWith("7") || withoutCountryCode.startsWith("8")) {
      return `0${withoutCountryCode.substring(0, 1)} ${withoutCountryCode.substring(1, 5)} ${withoutCountryCode.substring(5)}`;
    }
    // Format as 04XX XXX XXX for mobile numbers starting with 4
    else if (withoutCountryCode.startsWith("4")) {
      return `0${withoutCountryCode.substring(0, 3)} ${withoutCountryCode.substring(3, 6)} ${withoutCountryCode.substring(6)}`;
    }
  }
  
  // Handle standard Australian format (10 digits starting with 0)
  if (digits.startsWith("0") && digits.length === 10) {
    if (digits.startsWith("02") || digits.startsWith("03") || 
        digits.startsWith("07") || digits.startsWith("08")) {
      return `${digits.substring(0, 2)} ${digits.substring(2, 6)} ${digits.substring(6)}`;
    }
    else if (digits.startsWith("04")) {
      return `${digits.substring(0, 4)} ${digits.substring(4, 7)} ${digits.substring(7)}`;
    }
  }
  
  // Return original if no formatting rules match
  return phoneNumber;
};

interface Variant {
  id: number;
  title: string;
  option2: string;
}

interface Product {
  title: string;
  variants: Variant[];
}

interface Props {
  product: Product;
  selectedVariant?: number;
  onVariantChange?: (size: number) => void;
  onOpenChange?: (open: boolean) => void;
}

interface Store {
  id: string;
  name: string;
  address_street_1?: string;
  address_street_2?: string;
  city?: string;
  state?: string;
  postcode?: string | number;
  latitude?: number;
  longitude?: number;
  distance?: string | number;
  stock_status?: string;
  bg?: string;
  color?: string;
  phone_number?: string;
  store_email?: string;
  store_closing_hour?: string;
  sfs_enabled?: number;
  sfs_buffer?: number;
  clickandcollect_enabled?: number;
  clickandcollect_buffer?: number;
  virtual_enabled?: number;
  deptstore_enabled?: number;
  shopify_location_id?: string;

}

let unavailableSizes: string[] = [];

export const ProductFindInStoreModal: React.FC<Props> = ({ settings, onOpenChange }) => {
  const inputRef = useRef(null);
  const storeFinderRef = useRef<{ searchStores: () => void }>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [activeSize, setActiveSize] = useState<string | null>(null);
  const [storeResults, setStoreResults] = useState<any>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Helper function to check if store has sufficient stock for click and collect
  const isStoreAvailableForClickAndCollect = (store: Store, variantId: number) => {
    if (!store.clickandcollect_enabled) {
      console.log("Store does not have click and collect enabled");
      return false;
    }
    
    // Try multiple possible keys for store lookup
    const possibleKeys = [store.id, store.shopify_location_id, String(store.id)];
    let variantsAtLocation: any[] = [];
    let usedKey: string | null = null;
    
    for (const key of possibleKeys) {
      if (key && storeResults?.storesByLocation?.[key]) {
        variantsAtLocation = storeResults.storesByLocation[key];
        usedKey = key;
        break;
      }
    }
    
    const variantStock = variantsAtLocation.find(
      (variant: any) => String(variant.shopify_variant_id) === String(variantId)
    );
    
    if (!variantStock) {
      console.log("No variant stock found for variant ID:", variantId);
      return false;
    }
    const requiredStock = store.clickandcollect_buffer === 1 ? 2 : 1;
    const availableStock = (variantStock as any).free_stock || 0;

    const result = availableStock >= requiredStock;

    return result;
  };

  // Helper function to check if store has sufficient stock for SFS (Ship From Store)
  const isStoreAvailableForSFS = (store: Store, variantId: number) => {
    
    if (!store.sfs_enabled) {
      console.log("SFS not enabled for store");
      return false;
    }
    
    // Try multiple possible keys for store lookup, same as click and collect
    const possibleKeys = [store.id, store.shopify_location_id, String(store.id)];
    let variantsAtLocation: any[] = [];
    let usedKey: string | null = null;
    
    for (const key of possibleKeys) {
      if (key && storeResults?.storesByLocation?.[key]) {
        variantsAtLocation = storeResults.storesByLocation[key];
        usedKey = key;
        console.log("Found store data using key:", key);
        break;
      }
    }
    
    if (variantsAtLocation.length === 0) {
      return false;
    }
    
    const variantStock = variantsAtLocation.find(
      (variant: any) => String(variant.shopify_variant_id) === String(variantId)
    );
    
    if (!variantStock) {
      return false;
    }
    
    const requiredStock = store.sfs_buffer === 1 ? 2 : 1;
    const availableStock = variantStock.free_stock || 0;
    const result = availableStock >= requiredStock;
    
    return result;
  };

  const storesByLocation: Store[] = storeResults?.storesByLocation
    ? (Object.values(storeResults.storesByLocation)
        .flat()
        .filter((store) => {
          const storeTyped = store as Store;
          
          if (!Boolean(store)) {
            return false;
          }
          
          if (storeTyped.stock_status !== "In Stock") {
            return false;
          }
          
          // Check if store supports any fulfillment method
          const hasFulfillmentMethod = (
            storeTyped.clickandcollect_enabled === 1 ||
            storeTyped.sfs_enabled === 1 ||
            storeTyped.deptstore_enabled === 1
          );
          
          if (!hasFulfillmentMethod) {
            return false;
          }
          
          // If click and collect is enabled, check buffer requirement
          if (storeTyped.clickandcollect_enabled === 1 && activeSize) {
            const activeVariant = settings?.product?.variants.find((variant) => variant.option2 === activeSize);
            
            if (activeVariant && !isStoreAvailableForClickAndCollect(storeTyped, activeVariant.id)) {
              return false;
            }
            // console.log("Store passed Click and Collect check:", storeTyped.name);
          }
          
          // If SFS is enabled, check buffer requirement
          if (storeTyped.sfs_enabled === 1 && activeSize) {
            
            const activeVariant = settings?.product?.variants.find((variant) => variant.option2 === activeSize);
            if (activeVariant && !isStoreAvailableForSFS(storeTyped, activeVariant.id)) {
              return false;
            }
          }

          return true;
        }) as Store[])
    : [];

  // console.log('storeResults', storesByLocation)

  const variantIds = useMemo(() => {
    if (!activeSize) return [];
    const activeVariant = settings?.product?.variants.find((variant) => variant.option2 === activeSize);
    return activeVariant ? [activeVariant.id] : [];
  }, [settings, activeSize]);

  const variantSizes = useMemo(() => {
    return settings?.product?.variants
      ?.map((variant) => variant.option2)
      .filter((size, index, self) => size && self.indexOf(size) === index) || [];
  }, [settings]);

  const toggleModal = () => {
    setIsOpen((prev) => {
      const isNowOpen = !prev;
      const pdpDetails = document.querySelector('.section-main-product .shopify-section-main-product-inner .pdp-details') as HTMLElement;
      if (pdpDetails) {
        if (isNowOpen) {
          pdpDetails.classList.add('z-30');
        } else {
          pdpDetails.classList.remove('z-30');
        }
      }
      if (onOpenChange) onOpenChange(isNowOpen);
      return isNowOpen;
    });
  };

  const handleSizeSelect = (size: string) => {
    setActiveSize(size);
    setStoreResults({}); // Clear results before fetching
    setHasSearched(true);
    const input = document.querySelector(`input[name="Size-pdp-main"][value="${size}"]`) as HTMLInputElement;
    if (input) input.checked = true;

    setTimeout(() => {
      if (storeFinderRef.current?.searchStores) {
        // console.log('[FIS] search for size:', size);
        setIsLoading(true);
        storeFinderRef.current.searchStores();
      }
    }, 50);
  };

  const handleCustomAddToCartBtn = (store: Store) => {
    if (!activeSize) {
      alert("Please select a size first.");
      return;
    }
  
    const addToCartButton = document.querySelector('[data-add-to-cart]') as HTMLButtonElement | null;
  
    if (addToCartButton) {
      addToCartButton.click();
  
      // Trigger cart fetch update after short delay
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('liquid-ajax-cart:request-end'));
      }, 500);
    } else {
      console.error('Add to Cart button not found.');
    }
  };

  useEffect(() => {
    if (activeSize) setActiveSize(activeSize);

    const handleVariantChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { size } = customEvent.detail;
      setActiveSize(size);
    };

    window.addEventListener('variant:change', handleVariantChange);
    return () => window.removeEventListener('variant:change', handleVariantChange);
    setHasSearched(true);
  }, [activeSize]);

  useEffect(() => {
    document.body.classList.toggle('modal-open', isOpen);
    if (onOpenChange) onOpenChange(isOpen);
    return () => {
      document.body.classList.remove('modal-open');
      if (onOpenChange) onOpenChange(false);
    };
  }, [isOpen, onOpenChange]);

  useEffect(() => {
    function updateUnavailableForShipping(event: CustomEvent) {
      const unavailableForShipping = event.detail.unavailableForShipping || [];
      unavailableSizes = unavailableForShipping.map((id: string) => {
        const variant = settings?.product?.variants.find((v) => v.id == id);
        return variant ? variant.option2 : null;
      });
    }

    // @ts-ignore
    window.addEventListener('unavailableForShipping', updateUnavailableForShipping);

    return () => {
      // @ts-ignore
      window.removeEventListener('unavailableForShipping', updateUnavailableForShipping);
    };
  }, []);

  return (
    <div className=''>
      <button
        className='w-auto px-0 font-medium text-black uppercase cursor-pointer bc'
        onClick={toggleModal}
      >
        Find in store
      </button>

        <div className={`${isOpen ? 'fixed inset-0 bg-[#72757e]/70 modal-overlay transition-opacity duration-300' : ''}`}>
          <div className={`fixed top-0 right-0 w-full max-w-md h-full bg-white lg:max-w-[594px] transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className='flex items-center justify-between p-4 lg:p-8'>
              <h5 className='text-[#1D1F22]'>Find in Store</h5>
              <button className='w-auto p-0' onClick={toggleModal}>✕</button>
            </div>

            <div className='p-4 overflow-y-auto max-h-[calc(100vh-200px)] hide-scrollbar lg:px-8 lg:py-0'>
              <h3 className='font-medium b2'>Select size</h3>
              <div className='flex flex-wrap gap-2 mt-4 mb-6 lg:gap-3.5'>
                {variantSizes.map((size) => {
                  return (
                    <label
                      key={size}
                      className={`px-5 py-2.5 cursor-pointer ${activeSize === size ? 'bg-[#00263e] text-white' : 'bg-[#ecebeb] text-[#00263e]'} b2 font-normal flex items-center gap-2`}
                    >
                      <input
                        type='radio'
                        value={size}
                        checked={activeSize === size}
                        onChange={() => handleSizeSelect(size)}
                        className='!hidden'
                      />
                      {size}
                    </label>
                  );
                })}
              </div>

              {activeSize && (
                <>
                  <StoreFinder
                    ref={storeFinderRef}
                    layout="default"
                    variantIds={variantIds}
                    onStoresFetched={(fetchedStores) => {
                      if (fetchedStores) {
                        setStoreResults(fetchedStores);
                      }
                      setIsLoading(false);
                    }}
                    onSearch={() => setHasSearched(true)}
                  />
                  {isLoading && inputRef.current && inputRef.current.value.trim() !== "" && (
                    <div className="mb-4 text-black b3">
                      Checking store availability...
                    </div>
                  )}
                </>
              )}

              {!isLoading && storesByLocation.length > 0 && (
                <ul className="p-0 space-y-3 list-none">
                  {storesByLocation.map((store, index) => (
                    <li key={index} className="grid grid-cols-1 gap-4 py-4 border-t lg:grid-cols-[3fr_1.2fr]">
                      <div className='order-2 lg:order-none'>
                        <h6 className="mb-2 text-black">{store.name}</h6>

                        <p className="b3 text-[#54565a]">
                          {store.address_street_1}
                        </p>

                        <p className="b3 text-[#54565a]">
                          {store.address_street_2}
                        </p>

                        <p className="b3 text-[#54565a]">
                          {store.city}, {store.state}
                        </p>

                        <div className="flex flex-col gap-2 mt-4">
                          <div className='flex flex-wrap gap-x-4 b3'>
                            {store.phone_number && (
                              <a
                                href={`tel:${store.phone_number.replace(/\s+/g, '')}`}
                                className="text-[#00263e] flex items-center hover:no-underline"
                              >
                                <i className="mr-2 text-black icon icon-phone"></i>
                                <span className="underline underline-offset-2">{formatPhoneNumber(store.phone_number)}</span>
                              </a>
                            )}

                            {store.store_email && (
                              <a href={`mailto:${store.store_email}`} className='text-[#00263e] flex items-center hover:no-underline'>
                                <i className='mr-2 text-black icon icon-mail'></i>
                                <span className='underline underline-offset-2'>Email</span>
                              </a>
                            )}

                            <a
                              href={`https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#00263e] flex items-center hover:no-underline"
                            >
                              <i className="mr-2 text-black icon icon-location-wide"></i>
                              <span className="underline underline-offset-2">Get Directions</span>
                            </a>
                          </div>

                          <p className="b3 text-[#36573b] font-medium">
                            {store?.store_closing_hour}
                          </p>

                          <button
                            disabled={
                              unavailableSizes.includes(activeSize) || 
                              store.clickandcollect_enabled === 0 ||
                              (activeSize && !(() => {
                                const activeVariant = settings?.product?.variants.find((variant) => variant.option2 === activeSize);
                                return activeVariant ? isStoreAvailableForClickAndCollect(store, activeVariant.id) : false;
                              })())
                            }
                            className="fis-atc text-[#00263e] border border-[#00263e] font-medium b2 cursor-pointer hover:bg-[#00263e] hover:text-white lg:w-max"
                            onClick={() => {
                              handleCustomAddToCartBtn(store);
                            }}
                          >
                            ADD TO CART
                          </button>
                        </div>
                      </div>

                      <div className="flex items-center justify-between order-1 gap-2 lg:items-end lg:text-right lg:flex-col lg:justify-start lg:order-none">
                        <div className="flex items-center gap-1 b3 bg-[#f5f5f5] py-1 px-2">
                          {store.stock_status === "In Stock" ? (
                            <>
                              <i className='text-xl icon icon-check-circle'></i>
                              <span className='text-[#36573b]'>In stock</span>
                            </>
                          ) : (
                            <>
                              <i className='text-xl icon icon-cross-circle text-[#D12727]'></i>
                              <span className="text-[#D12727]">Out of stock</span>
                            </>
                          )}
                        </div>

                        {!unavailableSizes.includes(activeSize) && 
                         store.clickandcollect_enabled !== 0 &&
                         (() => {
                           if (!activeSize) return false;
                           const activeVariant = settings?.product?.variants.find((variant) => variant.option2 === activeSize);
                           return activeVariant ? isStoreAvailableForClickAndCollect(store, activeVariant.id) : false;
                         })() &&
                          <span className='text-[#36573b] blabel'>
                            Click & Collect Available
                          </span>
                        }
                      </div>
                    </li>
                  ))}
                </ul>
              )}

              {!isLoading && hasSearched && storesByLocation.length === 0 && (
                <p className="mt-4 text-black b3">
                  No available stores found.
                </p>
              )}
            </div>
          </div>
        </div>
    </div>
  );
};

export default ProductFindInStoreModal;